import React, { useEffect, useRef, useState, useId } from 'react';

const CMSV6_IP   = '103.6.245.78';
const CMSV6_PORT = 6605;
const STREAM_SUB = 1;   // substream — lower bandwidth, fine for multi-channel grid

// ── Global monkey-patch (reference-counted) ───────────────────
// The CMSV6 player constructs http(s)://CMSV6_IP:port/... URLs directly.
// In our HTTPS deployment those requests would be blocked as mixed content.
// We intercept them here and route through our backend proxies instead.
let _patchRefs = 0;
let _origFetch = null;
let _origWS    = null;

function _installPatch() {
  _patchRefs++;
  if (_patchRefs > 1) return;

  _origFetch = window.fetch;
  _origWS    = window.WebSocket;

  window.fetch = function patchedFetch(input, init) {
    const url = typeof input === 'string' ? input : input?.url ?? '';
    if (url.includes(CMSV6_IP)) {
      try {
        const p       = new URL(url);
        const devIdno = p.searchParams.get('DevIDNO') || p.searchParams.get('devIdno') || '';
        const channel = p.searchParams.get('Channel') || p.searchParams.get('channel') || '';
        return _origFetch(`/api/cmsv6/meta?devIdno=${devIdno}&channel=${channel}`, init);
      } catch {
        return _origFetch.apply(this, arguments);
      }
    }
    return _origFetch.apply(this, arguments);
  };

  function PatchedWebSocket(url, protocols) {
    let target = url;
    if (typeof url === 'string' && url.includes(CMSV6_IP)) {
      try {
        const p      = new URL(url);
        const origin = window.location.origin
          .replace('https://', 'wss://')
          .replace('http://',  'ws://');
        target = `${origin}/ws/cmsv6?${p.searchParams.toString()}`;
      } catch {}
    }
    return new _origWS(target, protocols);
  }
  PatchedWebSocket.prototype  = _origWS.prototype;
  PatchedWebSocket.CONNECTING = _origWS.CONNECTING;
  PatchedWebSocket.OPEN       = _origWS.OPEN;
  PatchedWebSocket.CLOSING    = _origWS.CLOSING;
  PatchedWebSocket.CLOSED     = _origWS.CLOSED;
  window.WebSocket = PatchedWebSocket;
}

function _removePatch() {
  _patchRefs = Math.max(0, _patchRefs - 1);
  if (_patchRefs > 0) return;
  if (_origFetch) { window.fetch     = _origFetch; _origFetch = null; }
  if (_origWS)    { window.WebSocket = _origWS;    _origWS    = null; }
}

// ── Script loader (singleton) ─────────────────────────────────
let _scriptPromise = null;
function _ensureScript() {
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve, reject) => {
    if (document.getElementById('cmsv6-sdk')) { resolve(); return; }
    const s    = document.createElement('script');
    s.id       = 'cmsv6-sdk';
    s.src      = '/cmsv6player.min.js';
    s.onload   = resolve;
    s.onerror  = () => reject(new Error('Failed to load /cmsv6player.min.js'));
    document.head.appendChild(s);
  });
  return _scriptPromise;
}

// ── Overlay button ────────────────────────────────────────────
function Btn({ title, onClick, active, activeColor = '#185FA5', children, style }) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        width: 26, height: 26, flexShrink: 0,
        border: 'none', borderRadius: 4,
        background: active ? activeColor : 'rgba(255,255,255,0.15)',
        color: active ? 'white' : '#ddd',
        cursor: 'pointer', fontSize: 13,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.15s',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ── Component ─────────────────────────────────────────────────
export default function CmsV6VideoPlayer({ vehicleId, channel, jsession }) {
  const uid    = useId().replace(/:/g, '');
  const domId  = `cmsv6-${uid}`;
  const wrapRef      = useRef(null);
  const playerRef    = useRef(null);
  const frameTimerRef = useRef(null);
  const audioRef = useRef({ talking: false });

  // Stream state
  const [status,   setStatus]   = useState('loading');
  const [errMsg,   setErrMsg]   = useState('');
  const [retryKey, setRetryKey] = useState(0);

  // Overlay UI state
  const [hovered,        setHovered]        = useState(false);
  const [isMuted,        setIsMuted]        = useState(false);
  const [isFlipped,      setIsFlipped]      = useState(false);
  const [isTalking,      setIsTalking]      = useState(false);
  const [volume,         setVolume]         = useState(50);
  const [showVolume,     setShowVolume]     = useState(false);
  const [snapFlash,      setSnapFlash]      = useState(false);

  // ── Overlay handlers ────────────────────────────────────────
  const handleMute = () => {
    if (!playerRef.current) return;
    try { playerRef.current.toolToggleAudio(0); } catch {}
    setIsMuted(m => !m);
    setShowVolume(false);
  };

  const handleVolumeChange = (e) => {
    if (!playerRef.current) return;
    const v = Number(e.target.value);
    try { playerRef.current.setVolume(0, v); } catch {}
    setVolume(v);
  };

  const handleSnap = () => {
    if (!playerRef.current) return;
    try { playerRef.current.toolSnap(0); } catch {}
    setSnapFlash(true);
    setTimeout(() => setSnapFlash(false), 200);
    setShowVolume(false);
  };

  const handleFlip = () => {
    if (!playerRef.current) return;
    try { playerRef.current.flipLeftRight(0); } catch {}
    setIsFlipped(f => !f);
    setShowVolume(false);
  };

  const handleTalk = () => {
    if (!playerRef.current) return;
    if (isTalking) {
      try { playerRef.current.stopTalkback(); } catch {}
      audioRef.current.talking = false;
      setIsTalking(false);
    } else {
      try { playerRef.current.startTalkback(); } catch {}
      audioRef.current.talking = true;
      setIsTalking(true);
    }
    setShowVolume(false);
  };

  // ── Main effect ─────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    // Reset overlay state for the new stream
    setIsMuted(false); setIsFlipped(false);
    setIsTalking(false);
    setVolume(50); setShowVolume(false); setSnapFlash(false);
    audioRef.current = { talking: false };

    async function start() {
      setStatus('loading');
      setErrMsg('');
      _installPatch();

      try {
        await _ensureScript();
      } catch (err) {
        if (!cancelled) { setStatus('error'); setErrMsg(err.message); }
        _removePatch();
        return;
      }

      const deadline = Date.now() + 4000;
      while (!window.Cmsv6Player && Date.now() < deadline) {
        await new Promise(r => setTimeout(r, 100));
      }

      if (!window.Cmsv6Player) {
        if (!cancelled) { setStatus('error'); setErrMsg('Player SDK loaded but window.Cmsv6Player not found'); }
        _removePatch();
        return;
      }

      if (cancelled || !wrapRef.current) { _removePatch(); return; }

      const w = wrapRef.current.clientWidth  || 640;
      const h = wrapRef.current.clientHeight || 360;

      try {
        const player = new window.Cmsv6Player({
          domId,
          width:             w,
          height:            h,
          live:              true,
          enableMse:         true,
          hideToobar:        true,
          hideSoundBtn:      true,
          hideFullScreenBtn: true,
          hideShotBtn:       true,
          singleRowNumber:   1,
          singleColNumber:   1,
        });

        playerRef.current = player;
        player.setWindowNum(1);
        try { player.setVolume(0, 50); } catch {}

        // startVideo(windowIndex, jsession, devIdno, channel, stream, aux, serverIp, serverPort)
        player.startVideo(0, jsession, String(vehicleId), Number(channel), STREAM_SUB, 0, CMSV6_IP, CMSV6_PORT);

        if (!cancelled) setStatus('live');

        // Poll for actual frames — startVideo() succeeds even on refused streams.
        let checks = 0;
        frameTimerRef.current = setInterval(() => {
          if (cancelled) { clearInterval(frameTimerRef.current); return; }
          checks++;

          const container = document.getElementById(domId);
          const video = container?.querySelector('video');
          if (video && video.readyState >= 2 && !video.paused) {
            clearInterval(frameTimerRef.current); return;
          }

          const canvas = container?.querySelector('canvas');
          if (canvas) {
            try {
              const ctx = canvas.getContext('2d');
              if (!ctx) { clearInterval(frameTimerRef.current); return; }
              const cw = Math.min(canvas.width, 32);
              const ch = Math.min(canvas.height, 32);
              if (cw > 0 && ch > 0) {
                const data = ctx.getImageData(0, 0, cw, ch).data;
                for (let j = 0; j < data.length; j += 4) {
                  if (data[j] > 10 || data[j + 1] > 10 || data[j + 2] > 10) {
                    clearInterval(frameTimerRef.current); return;
                  }
                }
              }
            } catch {
              clearInterval(frameTimerRef.current); return;
            }
          }

          if (checks >= 5) {
            clearInterval(frameTimerRef.current);
            if (!cancelled) { setStatus('error'); setErrMsg('Stream timeout — device may be busy'); }
          }
        }, 2000);

      } catch (err) {
        if (!cancelled) { setStatus('error'); setErrMsg(err.message || 'Player init failed'); }
        _removePatch();
      }
    }

    start();

    return () => {
      cancelled = true;
      clearInterval(frameTimerRef.current);
      const p = playerRef.current;
      if (p) {
        const { talking } = audioRef.current;
        try { if (talking) p.stopTalkback(); } catch {}
        try { p.stopVideo(0); } catch {}
        try { p.destory();    } catch {}   // SDK source typo
        playerRef.current = null;
      }
      _removePatch();
    };
  }, [vehicleId, channel, jsession, retryKey, domId]);

  // ── Render ──────────────────────────────────────────────────
  const overlayVisible = hovered || showVolume;

  return (
    <div
      ref={wrapRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setShowVolume(false); }}
      style={{ position: 'relative', width: '100%', height: '100%', background: '#0a0a0a', overflow: 'hidden' }}
    >
      {/* Player canvas host */}
      <div id={domId} style={{ width: '100%', height: '100%' }} />

      {/* Screenshot flash */}
      {snapFlash && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)', pointerEvents: 'none' }} />
      )}

      {/* Loading */}
      {status === 'loading' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, pointerEvents: 'none' }}>
          <div style={{ width: 26, height: 26, border: '2px solid #333', borderTopColor: '#185FA5', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <div style={{ color: '#555', fontSize: 11 }}>CH{channel + 1} connecting...</div>
        </div>
      )}

      {/* Error */}
      {status === 'error' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <div style={{ color: '#666', fontSize: 11, textAlign: 'center', padding: '0 10px' }}>{errMsg}</div>
          <button
            onClick={() => setRetryKey(k => k + 1)}
            style={{ padding: '3px 10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      {/* Channel label — top-left */}
      <div style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(0,0,0,.6)', color: '#ccc', fontSize: 9, padding: '2px 6px', borderRadius: 4, pointerEvents: 'none' }}>
        CH{channel + 1}{status === 'live' ? ' · LIVE' : ''}
      </div>

      {/* Control overlay — bottom, appears on hover, hidden during loading/error */}
      {status === 'live' && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
          padding: '18px 8px 6px',
          opacity: overlayVisible ? 1 : 0,
          transition: 'opacity 0.2s',
          pointerEvents: overlayVisible ? 'auto' : 'none',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>

            {/* 1 — Mute/Unmute */}
            <Btn title={isMuted ? 'Unmute' : 'Mute'} onClick={handleMute} active={isMuted}>
              {isMuted ? '🔇' : '🔊'}
            </Btn>

            {/* 2 — Volume (with popup slider) */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <Btn
                title="Volume"
                onClick={() => setShowVolume(v => !v)}
                active={showVolume}
              >
                🔉
              </Btn>
              {showVolume && (
                <div
                  onClick={e => e.stopPropagation()}
                  style={{
                    position: 'absolute', bottom: 32, left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0,0,0,0.85)', borderRadius: 6,
                    padding: '8px 6px',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  }}
                >
                  <span style={{ color: '#aaa', fontSize: 9 }}>{volume}</span>
                  {/* Vertical slider: rotate a horizontal range */}
                  <div style={{ width: 24, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'visible' }}>
                    <input
                      type="range" min="0" max="100" value={volume}
                      onChange={handleVolumeChange}
                      style={{
                        width: 80, height: 4,
                        transform: 'rotate(-90deg)',
                        cursor: 'pointer', accentColor: '#185FA5',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* 3 — Screenshot */}
            <Btn title="Screenshot" onClick={handleSnap}>
              📸
            </Btn>

            {/* 4 — Flip horizontal */}
            <Btn title="Flip" onClick={handleFlip} active={isFlipped} activeColor="#6c757d">
              🔁
            </Btn>

            {/* spacer */}
            <div style={{ flex: 1 }} />

            {/* 5 — Talkback */}
            <Btn title={isTalking ? 'Stop talkback' : 'Talkback'} onClick={handleTalk} active={isTalking} activeColor="#dc3545">
              🗣️
            </Btn>

          </div>
        </div>
      )}
    </div>
  );
}
